import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Typography, 
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { FaArrowLeft } from 'react-icons/fa';
import { MdExpandMore } from 'react-icons/md';
import { 
  Container, 
  Header, 
  HeaderLeft, 
  BackButton, 
  Title, 
  Content, 
  MainSection,
} from '../../common/styles';
import { useAlert } from '../../../contexts/AlertContext';
import { feedbackApi } from '../../../services/api';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import AssignReviewer from '../../FeedbackSessions/AssignReviewer';
import {
  getSessionStatus,
  FEEDBACK_SESSION_STATUSES
} from '../../../constants/feedbackSessionStatuses';
import { SessionStatusChip } from '../../common/SessionStatusChip';

const FeedbackEvaluators = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [loading, setLoading] = useState(true);
  const [feedbackSessions, setFeedbackSessions] = useState([]);
  const [expandedSession, setExpandedSession] = useState(null);
  const [employeeAssignmentsMap, setEmployeeAssignmentsMap] = useState(new Map());
  const [sessionLoadingStates, setSessionLoadingStates] = useState({});
  const [unsavedChanges, setUnsavedChanges] = useState({});
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingAccordionAction, setPendingAccordionAction] = useState(null);

  const fetchFeedbackSessions = useCallback(async () => {
    try {
      setLoading(true);
      const sessions = await feedbackApi.getUserConfigurationAssignments();
      setFeedbackSessions(sessions);
      
      // Initialize the employee assignments map with basic data
      const initialSessionMaps = new Map();
      sessions.forEach(session => {
        initialSessionMaps.set(session.id, new Map());
      });
      
      setEmployeeAssignmentsMap(initialSessionMaps);
    } catch (error) {
      console.error('Error fetching feedback sessions');
      showAlert(error.message || 'Failed to fetch feedback sessions', 'error');
      setFeedbackSessions([]);
    } finally {
      setLoading(false);
    }
  }, [showAlert]);

  // Process detailed session data to create employee assignments map for a specific session
  const processDetailedSessionData = (sessionId, sessionData, preserveExistingReviewers = false) => {
    
    // Get existing session map if we need to preserve reviewers
    const existingSessionMap = preserveExistingReviewers ? employeeAssignmentsMap.get(sessionId) : null;
    const hasExistingData = existingSessionMap && existingSessionMap.size > 0;
    
    const sessionMap = new Map();
    
    // Process employeeAssignments array
    if (sessionData.employeeAssignments && Array.isArray(sessionData.employeeAssignments)) {
      
      sessionData.employeeAssignments.forEach(assignment => {
        if (assignment.employee) {
          // Check if we have existing data for this employee
          const existingEmployeeData = existingSessionMap?.get(assignment.employee.id);
          const hasExistingReviewers = existingEmployeeData?.reviewers?.length > 0;
          
          // Create an entry for this employee
          const reviewers = (assignment.reviewers || []).map(reviewer => {
            return {
              reviewer: {
                ...reviewer.reviewer,
                assignment: reviewer.reviewer?.assignment || {
                  completedAt: null,
                  startedAt: null,
                  assignedAt: null,
                }
              },
              template: reviewer.template || null,
              reviewType: reviewer.reviewer?.assignment?.reviewType || null,
              order: reviewer.reviewer?.assignment?.order || null
            };
          });
          
          sessionMap.set(assignment.employee.id, {
            employee: assignment.employee,
            matrix: assignment.matrix || null,
            assignment: assignment.assignment || {
              completedAt: null,
              startedAt: null,
              assignedAt: null,
              status: null,
            },
            // Use existing reviewers if available and we're preserving them
            reviewers: (preserveExistingReviewers && hasExistingReviewers) 
              ? existingEmployeeData.reviewers 
              : reviewers
          });
        } 

      });
    }
    // If we're preserving existing reviewers, make sure we include all employees from the existing map
    // This ensures we don't lose any employees that might not be in the new data
    if (preserveExistingReviewers && hasExistingData) {
      existingSessionMap.forEach((employeeData, employeeId) => {
        if (!sessionMap.has(employeeId)) {
          sessionMap.set(employeeId, employeeData);
        }
      });
    }
    
    // Update the employee assignments map with the detailed data for this session
    setEmployeeAssignmentsMap(prevMap => {
      const newMap = new Map(prevMap);
      newMap.set(sessionId, sessionMap);
      return newMap;
    });
  };

  useEffect(() => {
    fetchFeedbackSessions();
  }, [fetchFeedbackSessions]);

  const handleBack = () => {
    navigate('/');
  };

  const handleAccordionChange = (sessionId) => async (event, isExpanded) => {
    
    // Check if we're closing the accordion and there are unsaved changes
    if (!isExpanded && expandedSession === sessionId && unsavedChanges[sessionId]) {
      
      // Store the pending action to be executed after confirmation
      setPendingAccordionAction({
        sessionId,
        action: 'close'
      });
      
      // Show confirmation dialog
      setConfirmDialogOpen(true);
      
      // Prevent accordion from closing until user confirms
      event.preventDefault();
      return;
    }
    
    // Check if we're opening a different accordion while the current one has unsaved changes
    if (isExpanded && expandedSession !== null && expandedSession !== sessionId && unsavedChanges[expandedSession]) {
      
      // Store the pending action to be executed after confirmation
      setPendingAccordionAction({
        sessionId,
        action: 'open'
      });
      
      // Show confirmation dialog
      setConfirmDialogOpen(true);
      
      // Prevent accordion from opening until user confirms
      event.preventDefault();
      return;
    }
    
    // Update expanded session state
    setExpandedSession(isExpanded ? sessionId : null);
    
    if (isExpanded) {
      
      // Debug the current state of the employeeAssignmentsMap for this session
      
      // Set loading state for this session
      setSessionLoadingStates(prev => ({ ...prev, [sessionId]: true }));
      
      try {
        // Fetch detailed session data
        const sessionData = await feedbackApi.getMySessionById(sessionId);
        
        
        // Process the detailed data WITHOUT preserving existing reviewers
        // This ensures we use fresh data from the API
        processDetailedSessionData(sessionId, sessionData, false);
        
        // Clear any unsaved changes for this session since we're using fresh data
        setUnsavedChanges(prev => {
          const updated = { ...prev };
          delete updated[sessionId];
          return updated;
        });
        
        // Debug the updated state of the employeeAssignmentsMap for this session
      } catch (error) {
        console.error('Error fetching session details:', error);
        showAlert(error.message || 'Failed to fetch session details', 'error', error);
        
        // If we failed to fetch data but have existing data, we can still show it
        const existingSessionMap = employeeAssignmentsMap.get(sessionId);
        const hasExistingData = existingSessionMap && existingSessionMap.size > 0;
        
        if (!hasExistingData) {
          // If we don't have existing data, show an error
          showAlert('Failed to load session data and no cached data is available', 'error', error);
        }
      } finally {
        // Clear loading state
        setSessionLoadingStates(prev => ({ ...prev, [sessionId]: false }));
      }
    }
  };

  const handleReviewerAssignment = (sessionId, employeeId, updatedReviewers) => {
    
    // Get the current session map
    const sessionMap = employeeAssignmentsMap.get(sessionId);
    if (!sessionMap) {
      console.error('No session map found for session:', sessionId);
      return;
    }
    
    // Update the reviewers for the employee
    const employeeAssignment = sessionMap.get(employeeId);
    if (!employeeAssignment) {
      console.error('No employee assignment found for employee:', employeeId);
      return;
    }
    
    
    // Create a new object to ensure React detects the change
    const updatedEmployeeAssignment = {
      ...employeeAssignment,
      reviewers: updatedReviewers
    };
    
    // Update the map with a new Map to ensure React detects the change
    const updatedSessionMap = new Map(sessionMap);
    updatedSessionMap.set(employeeId, updatedEmployeeAssignment);
    
    setEmployeeAssignmentsMap(prevMap => {
      const newMap = new Map(prevMap);
      newMap.set(sessionId, updatedSessionMap);
      return newMap;
    });
    
    // Mark this session as having unsaved changes
    setUnsavedChanges(prev => ({
      ...prev,
      [sessionId]: true
    }));
  };

  // Save reviewer assignments for a specific session
  const saveReviewerAssignments = async (sessionId) => {
    
    // Set loading state for this session
    setSessionLoadingStates(prev => ({ ...prev, [sessionId]: true }));
    
    try {
      // Get the session map
      const sessionMap = employeeAssignmentsMap.get(sessionId);
      if (!sessionMap || sessionMap.size === 0) {
        console.error('No employee assignments map for session:', sessionId);
        showAlert('No employee assignments to save', 'error');
        return;
      }
      
      // Convert the Map to the format expected by the API
      const employeeAssignments = Array.from(sessionMap.entries()).map(([employeeId, data]) => {
        return {
          employeeId,
          matrix: data.matrix,
          reviewers: data.reviewers.map(reviewer => ({
            reviewer: {
              id: reviewer.reviewer.id,
              fullName: reviewer.reviewer.fullName
            },
            template: reviewer.template,
            reviewType: reviewer.reviewType,
            order: reviewer.order
          }))
        };
      });
      
      
      // Call the API to save the assignments
      await feedbackApi.updateFeedbackSessionMyAssignments(sessionId, employeeAssignments);
      
      // Clear unsaved changes for this session
      setUnsavedChanges(prev => {
        const updated = { ...prev };
        delete updated[sessionId];
        return updated;
      });
      
      showAlert('Reviewer assignments saved successfully', 'success');
    } catch (error) {
      console.error('Error saving reviewer assignments:', error);
      showAlert(error.message || 'Failed to save reviewer assignments', 'error', error);
    } finally {
      // Clear loading state
      setSessionLoadingStates(prev => ({ ...prev, [sessionId]: false }));
    }
  };

  // Handle confirmation dialog actions
  const handleConfirmSave = async () => {
    if (pendingAccordionAction) {
      const { sessionId, action } = pendingAccordionAction;
      
      try {
        // Save the changes
        await saveReviewerAssignments(sessionId);
        
        // Close the dialog
        setConfirmDialogOpen(false);
        
        // If the action was to close the accordion, close it now
        if (action === 'close') {
          setExpandedSession(null);
        } else if (action === 'open') {
          // If the action was to open a different accordion, open it now
          setExpandedSession(sessionId);
          
          // Fetch the session data if needed
          if (!sessionLoadingStates[sessionId]) {
            setSessionLoadingStates(prev => ({ ...prev, [sessionId]: true }));
            try {
              const sessionData = await feedbackApi.getMySessionById(sessionId);
              processDetailedSessionData(sessionId, sessionData);
            } catch (error) {
              console.error(`Error fetching session ${sessionId}:`, error);
              showAlert(`Failed to load session details: ${error.message}`, 'error', error);
            } finally {
              setSessionLoadingStates(prev => ({ ...prev, [sessionId]: false }));
            }
          }
        }
        
        // Clear the pending action
        setPendingAccordionAction(null);
      } catch (error) {
        console.error('Error saving reviewer assignments:', error);
        showAlert(`Failed to save changes: ${error.message}`, 'error', error);
      }
    } else {
      // Just close the dialog if there's no pending action
      setConfirmDialogOpen(false);
      setPendingAccordionAction(null);
    }
  };
  
  const handleConfirmDiscard = async () => {
    if (pendingAccordionAction) {
      const { sessionId, action } = pendingAccordionAction;
      
      // Clear unsaved changes for this session
      setUnsavedChanges(prev => {
        const updated = { ...prev };
        delete updated[sessionId];
        return updated;
      });
      
      // Close the dialog
      setConfirmDialogOpen(false);
      
      // Execute the pending action
      if (action === 'close') {
        // Close the accordion
        setExpandedSession(null);
      } else if (action === 'open') {
        // Open the accordion and fetch fresh data
        setExpandedSession(sessionId);
        
        // Fetch the session data
        setSessionLoadingStates(prev => ({ ...prev, [sessionId]: true }));
        try {
          const sessionData = await feedbackApi.getMySessionById(sessionId);
          processDetailedSessionData(sessionId, sessionData);
        } catch (error) {
          console.error(`Error fetching session ${sessionId}:`, error);
          showAlert(`Failed to load session details: ${error.message}`, 'error', error);
        } finally {
          setSessionLoadingStates(prev => ({ ...prev, [sessionId]: false }));
        }
      }
      
      // Clear the pending action
      setPendingAccordionAction(null);
    } else {
      // Just close the dialog if there's no pending action
      setConfirmDialogOpen(false);
      setPendingAccordionAction(null);
    }
  };
  
  const handleCancelDialog = () => {
    // Close the dialog without doing anything
    setConfirmDialogOpen(false);
    setPendingAccordionAction(null);
  };

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={handleBack}>
            <FaArrowLeft /> Back to Home
          </BackButton>
          <Title>Configure Feedback Evaluators</Title>
        </HeaderLeft>
      </Header>
      <Content>
        <MainSection>
          {loading ? (
            <LoadingSpinner />
          ) : feedbackSessions.length > 0 ? (
            <>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 2
              }}>
                <Typography 
                  sx={{ 
                    color: '#64748B',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {feedbackSessions.length}
                  </Box>
                  {feedbackSessions.length === 1 ? 'session' : 'sessions'} found
                </Typography>
              </Box>
              
              {/* Render sessions as accordions */}
              <Box sx={{ mt: 2 }}>
                {feedbackSessions.map((session) => {
                  const sessionId = session.id;
                  const isExpanded = expandedSession === sessionId;
                  const status = getSessionStatus(session);
                  const isSessionLoading = sessionLoadingStates[sessionId];
                  
                  return (
                    <Box 
                      key={sessionId} 
                      sx={{ 
                        position: 'relative',
                        mb: 2,
                      }}
                    >
                      <Accordion 
                        expanded={isExpanded}
                        onChange={handleAccordionChange(sessionId)}
                        sx={{
                          boxShadow: 'none',
                          border: '1px solid #E2E8F0',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          '&:before': {
                            display: 'none',
                          },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<MdExpandMore style={{ fontSize: '24px' }} />}
                          sx={{
                            backgroundColor: '#F8FAFC',
                            borderBottom: isExpanded ? '1px solid #E2E8F0' : 'none',
                            pr: 12, // Add more padding to the right to make room for both the status chip and expand icon
                            minHeight: '64px',
                            '& .MuiAccordionSummary-expandIconWrapper': {
                              color: '#64748B',
                              '&.Mui-expanded': {
                                transform: 'rotate(180deg)',
                              }
                            },
                            '& .MuiAccordionSummary-content': {
                              margin: '12px 0',
                            }
                          }}
                        >
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            width: '100%'
                          }}>
                            <Box sx={{ 
                              display: 'flex', 
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              width: '100%'
                            }}>
                              <Typography sx={{ fontWeight: 600, color: '#1E293B' }}>
                                {session.name}
                                {unsavedChanges[sessionId] && (
                                  <Box 
                                    component="span" 
                                    sx={{ 
                                      display: 'inline-block',
                                      ml: 1,
                                      width: '8px',
                                      height: '8px',
                                      borderRadius: '50%',
                                      backgroundColor: '#FFD700',
                                    }}
                                  />
                                )}
                              </Typography>
                            </Box>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              mt: 0.5,
                              color: '#64748B',
                              fontSize: '0.875rem',
                            }}>
                              <Typography variant="body2" sx={{ color: '#64748B' }}>
                                {session.businessUnit?.name}
                              </Typography>
                              <Box component="span" sx={{ mx: 1 }}>•</Box>
                              <Typography variant="body2" sx={{ color: '#64748B' }}>
                                {session.month}/{session.year}
                              </Typography>
                              <Box component="span" sx={{ mx: 1 }}>•</Box>
                              <Typography variant="body2" sx={{ color: '#64748B' }}>
                                {session.reviewType} Review
                              </Typography>
                            </Box>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 3 }}>
                          {/* Show loading spinner while fetching session details */}
                          {isSessionLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                              <LoadingSpinner message="Loading session details..." />
                            </Box>
                          ) : (
                            /* Render AssignReviewer component for this session */
                            session.businessUnit && employeeAssignmentsMap.has(sessionId) ? (
                              <>
                                
                                {employeeAssignmentsMap.get(sessionId).size > 0 ? (
                                  <>
                                    <AssignReviewer
                                      businessUnit={session.businessUnit}
                                      employeeAssignments={employeeAssignmentsMap.get(sessionId)}
                                      onReviewerAssignment={(employeeId, updatedReviewers) => 
                                        handleReviewerAssignment(sessionId, employeeId, updatedReviewers)
                                      }
                                      reviewType={session.reviewType}
                                      readOnly={status !== FEEDBACK_SESSION_STATUSES.CONFIGURATION}
                                      forceAsEmployee={true}
                                      selectedSessionTemplate={session.skillsMatrixSession}
                                    />
                                    
                                    {status === FEEDBACK_SESSION_STATUSES.CONFIGURATION && (
                                      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button
                                          variant="contained"
                                          color="primary"
                                          onClick={() => saveReviewerAssignments(sessionId)}
                                          disabled={isSessionLoading || !unsavedChanges[sessionId]}
                                          startIcon={unsavedChanges[sessionId] ? <span style={{ color: 'yellow', fontSize: '8px' }}>●</span> : null}
                                        >
                                          {isSessionLoading ? 'Saving...' : 'Save Changes'}
                                        </Button>
                                      </Box>
                                    )}
                                  </>
                                ) : (
                                  <Box sx={{ p: 3, textAlign: 'center' }}>
                                    <EmptyState
                                      title="No Individuals Found"
                                      description="There are no individuals available for this feedback session."
                                    />
                                  </Box>
                                )}
                              </>
                            ) : (
                              <Box sx={{ p: 2 }}>
                                <Typography color="error">
                                  {!session.businessUnit 
                                    ? "Missing business unit information for this session." 
                                    : !employeeAssignmentsMap.has(sessionId)
                                      ? "No individual assignments map for this session."
                                      : employeeAssignmentsMap.get(sessionId).size === 0
                                        ? "No individuals found for this session."
                                        : "Unknown error rendering session data."}
                                </Typography>
                                
                              </Box>
                            )
                          )}
                        </AccordionDetails>
                      </Accordion>
                      {/* Position the status chip absolutely to the top right */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 16,
                          right: 48,
                          zIndex: 1,
                        }}
                      >
                        <SessionStatusChip
                          session={session}
                          sx={{
                            borderRadius: '16px',
                            height: '32px'
                          }}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </>
          ) : (
            <EmptyState
              title="No Feedback Sessions Found"
              description="You don't have any feedback sessions yet."
            />
          )}
        </MainSection>
      </Content>
      
      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelDialog}
        aria-labelledby="unsaved-changes-dialog-title"
        aria-describedby="unsaved-changes-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: '8px',
            maxWidth: '500px',
            width: '100%'
          }
        }}
      >
        <DialogTitle 
          id="unsaved-changes-dialog-title"
          sx={{ 
            fontWeight: 600, 
            color: '#1E293B',
            borderBottom: '1px solid #E2E8F0',
            p: 3
          }}
        >
          Unsaved Changes
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 2 }}>
          <DialogContentText 
            id="unsaved-changes-dialog-description"
            sx={{ color: '#64748B' }}
          >
            You have unsaved changes to your reviewer assignments. What would you like to do?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, px: 3, borderTop: '1px solid #E2E8F0' }}>
          <Button 
            onClick={handleCancelDialog} 
            sx={{ 
              color: '#64748B',
              '&:hover': {
                backgroundColor: '#F8FAFC'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDiscard} 
            color="error"
            sx={{
              '&:hover': {
                backgroundColor: '#FEE2E2'
              }
            }}
          >
            Discard Changes
          </Button>
          <Button 
            onClick={handleConfirmSave} 
            variant="contained" 
            color="primary"
            sx={{
              backgroundColor: '#3B82F6',
              '&:hover': {
                backgroundColor: '#2563EB'
              }
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FeedbackEvaluators; 